// Prevent vertical scrolling when interacting with elements
// marked by data-no-vert-scroll. Helps stop rubber-banding
// caused by dragging on sticky header or composer on mobile.

export function initNoVerticalScroll() {
  if (typeof document === 'undefined') return;

  let lastX = 0;
  let lastY = 0;

  const getPath = (e: Event): EventTarget[] => {
    try {
      const p = (e as any).composedPath?.();
      if (Array.isArray(p) && p.length) return p;
    } catch {}
    return [];
  };

  const onStart = (e: TouchEvent) => {
    try {
      const t = e.touches?.[0];
      if (!t) return;
      lastX = t.clientX;
      lastY = t.clientY;
    } catch {}
  };

  const onMove = (e: TouchEvent) => {
    try {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - lastX;
      const dy = t.clientY - lastY;
      lastX = t.clientX;
      lastY = t.clientY;

      // Don't interfere with mostly-horizontal gestures.
      if (Math.abs(dx) > Math.abs(dy)) return;

      const path = getPath(e);
      const nodes: HTMLElement[] = [];
      for (const n of path) {
        if (n && (n as any).nodeType === 1) nodes.push(n as HTMLElement);
      }

      // Fallback for browsers without composedPath (should be rare).
      if (!nodes.length) {
        let el: HTMLElement | null = e.target as HTMLElement | null;
        while (el) {
          nodes.push(el);
          el = el.parentElement;
        }
      }

      // Only activate lock inside regions explicitly marked.
      const inLockedRegion = nodes.some((el) => el.hasAttribute('data-no-vert-scroll'));
      if (!inLockedRegion) return;

      // Find the nearest scrollable ancestor in the composed path.
      let scrollEl: HTMLElement | null = null;
      for (const el of nodes) {
        const style = window.getComputedStyle(el);
        const oy = style.overflowY;
        const isScrollable = (oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight;
        if (isScrollable) {
          scrollEl = el;
          break;
        }
      }

      // If nothing can scroll, block to avoid body/viewport rubber-banding.
      if (!scrollEl) {
        e.preventDefault();
        return;
      }

      const maxScroll = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
      const scrollTop = scrollEl.scrollTop;
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop >= maxScroll - 1;

      // Prevent scroll chaining beyond the scroll container edges.
      if ((atTop && dy > 0) || (atBottom && dy < 0)) {
        e.preventDefault();
      }
    } catch {}
  };

  document.addEventListener('touchstart', onStart, { passive: true, capture: true });
  document.addEventListener('touchmove', onMove, { passive: false, capture: true });
}

if (typeof window !== 'undefined') {
  try { initNoVerticalScroll(); } catch {}
}
